import type { KonvaEventObject } from "konva/lib/Node";

import { AnnotationStrategy } from "./AnnotationStrategy";
import { useAnnotationStore } from "../store/annotationStore";
import { createBoxAnnotation } from "../store/annotationTypes";
import { NORMALIZED_BOUNDS } from "../utils/coordinates";

export class BoxAnnotationStrategy extends AnnotationStrategy {
  getAnnotationType(): string {
    return "box";
  }

  handlePointerDown(e: KonvaEventObject<PointerEvent>): void {
    const stage = e.target.getStage();
    const pointerPos = stage?.getPointerPosition();
    if (!pointerPos) return;

    const normalized = this.props.screenToNormalized(pointerPos.x, pointerPos.y);

    // Check if click is within image bounds
    if (
      normalized.x >= NORMALIZED_BOUNDS.MIN &&
      normalized.x <= NORMALIZED_BOUNDS.MAX &&
      normalized.y >= NORMALIZED_BOUNDS.MIN &&
      normalized.y <= NORMALIZED_BOUNDS.MAX
    ) {
      this.props.setDrawingState({
        isDrawing: true,
        startPoint: normalized,
        tempBox: { x: normalized.x, y: normalized.y, width: 0, height: 0 },
      });
    }
  }

  handlePointerMove(e: KonvaEventObject<PointerEvent>): void {
    // Read from ref for current state during drag operations
    const drawingState = this.props.drawingStateRef.current;
    if (!drawingState.isDrawing || !drawingState.startPoint) return;

    const stage = e.target.getStage();
    const pointerPos = stage?.getPointerPosition();
    if (!pointerPos) return;

    const normalized = this.props.screenToNormalized(pointerPos.x, pointerPos.y);

    // Clamp to image bounds
    const clampedX = Math.max(NORMALIZED_BOUNDS.MIN, Math.min(NORMALIZED_BOUNDS.MAX, normalized.x));
    const clampedY = Math.max(NORMALIZED_BOUNDS.MIN, Math.min(NORMALIZED_BOUNDS.MAX, normalized.y));

    // Use setDrawingState which handles both ref update and DOM manipulation
    // This is already optimized to skip React re-renders during dragging
    this.props.setDrawingState({
      ...drawingState,
      tempBox: {
        x: Math.min(drawingState.startPoint.x, clampedX),
        y: Math.min(drawingState.startPoint.y, clampedY),
        width: Math.abs(clampedX - drawingState.startPoint.x),
        height: Math.abs(clampedY - drawingState.startPoint.y),
      },
    });
  }

  handlePointerUp(_e: KonvaEventObject<PointerEvent>): void {
    // PERFORMANCE OPTIMIZATION: Get the latest state from the ref
    // During dragging, only the ref is updated (not React state) to avoid re-renders.
    // Here we read from the ref to get the final tempBox dimensions.
    const currentDrawingState = this.props.drawingStateRef.current;
    const { addAnnotation } = this.props;

    if (!currentDrawingState.isDrawing || !currentDrawingState.tempBox) {
      return;
    }

    // Only add annotation if it has meaningful size
    if (currentDrawingState.tempBox.width > 10 && currentDrawingState.tempBox.height > 10) {
      // Get current annotations from store for accurate count
      const currentAnnotations = useAnnotationStore.getState().getAnnotations();
      const boxCount = currentAnnotations.filter((a) => a.type === "box").length;
      addAnnotation(
        createBoxAnnotation({
          x: currentDrawingState.tempBox.x,
          y: currentDrawingState.tempBox.y,
          width: currentDrawingState.tempBox.width,
          height: currentDrawingState.tempBox.height,
          label: `Box ${boxCount + 1}`,
        }),
      );
    }

    // Reset drawing state
    this.props.setDrawingState({
      isDrawing: false,
      startPoint: null,
      tempBox: null,
    });
  }
}
